import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";

import { CodeEditorRef } from "../../components/canvas/CodeEditor";
import DefaultLayout from "../../components/nav/DefaultLayout";
import ProjectsManager from "../../components/projects/ProjectsManager";
import { dbStorage } from "../../utils/dbStorage";
import { observer } from "mobx-react-lite";
import { projectService } from "../../services/ProjectService";
import { rootStore } from "../../stores/RootStore";

const ProjectsPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const params = useParams({ from: '/org/$orgSlug' });
    const orgSlug = params.orgSlug as string;
    const { projectStore, uiStore } = rootStore;
    const codeEditorRef = useRef<CodeEditorRef>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [hasCheckedPendingPrompt, setHasCheckedPendingPrompt] = useState(false);

    useEffect(() => {
        const checkPendingPrompt = async () => {
            if (hasCheckedPendingPrompt) return;

            try {
                const hasPending = await dbStorage.hasPendingPrompt();
                if (hasPending) {
                    const prompt = await dbStorage.getPendingPrompt();
                    if (prompt) {
                        await dbStorage.clearPendingPrompt();

                        navigate({
                            to: '/org/$orgSlug/create-new-project',
                            params: { orgSlug },
                            search: { type: 'prompt', prompt }
                        });
                        return;
                    }
                }
                setHasCheckedPendingPrompt(true);
            } catch (error) {
                console.error('Error checking pending prompt:', error);
                setHasCheckedPendingPrompt(true);
            }
        };

        checkPendingPrompt();
    }, [navigate, orgSlug, hasCheckedPendingPrompt]);

    // Memoize callback functions to prevent recreating on each render
    const handleCreateNewProject = useCallback(async (type: string, prompt?: string) => {
        const searchParams: { type: string; prompt?: string } = { type };
        if (prompt) {
            searchParams.prompt = prompt;
        }

        navigate({
            to: '/org/$orgSlug/create-new-project',
            params: { orgSlug },
            search: searchParams
        });
    }, [navigate, orgSlug]);

    const handleLoadProject = useCallback(async (projectId: string) => {
        try {
            // Fetch project metadata first to check its status
            const projectMetadata = await projectService.getProject(projectId);

            if (projectMetadata.isPublished) {
                // For published projects, navigate directly to the details page
                console.log(`Navigating to details for published project: ${projectId}`);
                navigate({
                    to: '/org/$orgSlug/project-details/$projectId',
                    params: {
                        orgSlug,
                        projectId
                    }
                });
            } else {
                // For draft projects, load project data into the store and navigate to the editor
                console.log(`Loading draft project into editor: ${projectId}`);
                // This inherently involves Pyodide via projectStore.loadProject
                await projectStore.loadProject(
                    projectId,
                    uiStore.updateLogs.bind(uiStore),
                    uiStore.setActiveTab.bind(uiStore)
                );
                console.log(`Navigating to editor for draft project: ${projectId}`);
                navigate({
                    to: '/org/$orgSlug/$projectId',
                    params: {
                        orgSlug,
                        projectId
                    }
                });
            }
        } catch (error) {
            console.error(`Failed to load or navigate project ${projectId}:`, error);
            // Optionally, add user feedback like a toast notification
        }
    }, [projectStore, uiStore, navigate, orgSlug]);

    const handleReturnToProjects = useCallback(() => {
        projectStore.returnToProjects();
    }, [projectStore]);

    const handlePreviewApp = useCallback(() => {
        console.log('Preview app functionality to be implemented');
    }, []);

    // Memoize the rendered components to prevent unnecessary rerenders
    // const templateContent = useMemo(() => (
    //     <TemplateContent onCreateNewProject={handleCreateNewProject} />
    // ), [handleCreateNewProject]);

    const projectsManager = useMemo(() => (
        <ProjectsManager
            onSelectProject={handleLoadProject}
            onCreateNewProject={handleCreateNewProject}
            codeEditorRef={codeEditorRef}
            onGeneratingChange={setIsGenerating}
        />
    ), [handleLoadProject, handleCreateNewProject, codeEditorRef]);

    return (
        <DefaultLayout
            onNewProject={() => handleCreateNewProject('blank')}
            onOpenProject={handleReturnToProjects}
            onOpenPreview={handlePreviewApp}
        >
            <div className="space-y-8">
                {projectsManager}
            </div>
        </DefaultLayout>
    );
});

export default React.memo(ProjectsPage);
